import { Box, Stack, Typography, alpha, useTheme, useMediaQuery } from '@mui/material';
import type { ReactNode } from 'react';

interface Props {
  title?: string;
  subtitle?: string;
  footer?: ReactNode;
  children: ReactNode;
  sideContent?: ReactNode;
}

export default function AuthLayout({
  title = 'Super SaaS',
  subtitle = 'Enterprise operations platform',
  footer,
  children,
  sideContent,
}: Props) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLight = theme.palette.mode === 'light';
  const primary = theme.palette.primary.main;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(340px, 42%) 1fr' },
        bgcolor: isLight ? '#F4F6FA' : theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-end', md: 'center' },
          px: { xs: 3, sm: 4.5, md: 6.5 },
          py: { xs: 3.5, md: 7 },
          minHeight: { xs: 220, md: '100vh' },
          background: `
            radial-gradient(900px 520px at 0% 0%, ${alpha(primary, 0.42)} 0%, transparent 55%),
            radial-gradient(700px 420px at 100% 100%, ${alpha(theme.palette.secondary.main, 0.28)} 0%, transparent 50%),
            linear-gradient(168deg, #070B14 0%, #0C1222 52%, #101828 100%)
          `,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.22,
            backgroundImage: `
              linear-gradient(${alpha('#fff', 0.06)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha('#fff', 0.06)} 1px, transparent 1px)
            `,
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse at 30% 40%, black 10%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 420,
            height: 420,
            borderRadius: '50%',
            right: -140,
            top: -160,
            background: `radial-gradient(circle, ${alpha('#fff', 0.08)} 0%, transparent 68%)`,
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 440 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={{ xs: 2.5, md: 5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha('#fff', 0.1),
                border: `1px solid ${alpha('#fff', 0.16)}`,
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 700,
                fontSize: '1.15rem',
                letterSpacing: '-0.04em',
              }}
            >
              S
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                }}
              >
                {title}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.62), letterSpacing: '0.02em' }}>
                {subtitle}
              </Typography>
            </Box>
          </Stack>

          {isDesktop && sideContent}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.25, sm: 4, md: 6 },
          py: { xs: 3.5, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Box
            sx={{
              bgcolor: isLight ? '#fff' : alpha(theme.palette.background.paper, 0.92),
              border: `1px solid ${isLight ? alpha('#0B1220', 0.07) : alpha('#fff', 0.08)}`,
              borderRadius: '20px',
              boxShadow: isLight
                ? '0 1px 2px rgba(11,18,32,0.04), 0 18px 48px rgba(11,18,32,0.06)'
                : '0 18px 48px rgba(0,0,0,0.35)',
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
          {footer && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={2.25}
              fontWeight={500}
              sx={{ letterSpacing: '0.01em' }}
            >
              {footer}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
